<template>
  <div class="base-card-info">
    <div class="info-card-title">
      开票申请
    </div>
    <div style="height: 100%; padding: 0;">
      <!-- 搜索条件区域 -->
      <search-container :key="serverId">
        <search-col>
          <el-input v-model="params.orderDetailNo" placeholder="子订单编号" clearable @keydown="onEnter" />
        </search-col>
        
        <search-col>
          <el-select
            v-model="params.companyId"
            placeholder="企业名称"
            clearable
            filterable
          >
            <el-option
              v-for="item in companyList"
              :key="item.id"
              :label="item.companyName"
              :value="item.id"
            />
          </el-select>
        </search-col>

        <search-col>
          <el-input v-model="params.freelancer" placeholder="自由职业者" clearable @keydown="onEnter" />
        </search-col>

        <search-col>
          <el-input v-model="params.dutyNo" placeholder="任务编号" clearable @keydown="onEnter" />
        </search-col>

        <search-col>
          <el-select 
            v-model="params.invoiceSubId" 
            placeholder="订单开票科目" 
            clearable 
            filterable
          >
            <el-option 
              v-for="item in invoiceSubjectList" 
              :key="item.invoiceSubId" 
              :label="item.invoiceSubName" 
              :value="item.invoiceSubId"
            />
          </el-select>
        </search-col>

        <search-col>
          <el-date-picker
            v-model="dateRange"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD HH:mm:ss"
            :default-time="defaultTimeFormat"
            style="width: 100%;"
            :disabled-date="disabledDate"
            type="daterange"
            range-separator="-"
            start-placeholder="创建时间(起)"
            end-placeholder="创建时间(止)"
          />
        </search-col>

        <search-col>
          <el-date-picker
            v-model="dateConfirmRange"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD HH:mm:ss"
            :default-time="defaultTimeFormat"
            style="width: 100%;"
            :disabled-date="disabledDate"
            type="daterange"
            range-separator="-"
            start-placeholder="完成时间(起)"
            end-placeholder="完成时间(止)"
          />
        </search-col>

        <template v-if="isHnOfflineInvoice">
          <search-col>
            <el-select 
              v-model="params.inHnAuthTime" 
              placeholder="本人授权有效期" 
              clearable 
              filterable
            >
              <el-option 
                label="授权有效期内" 
                :value="true"
              />
              <el-option 
                label="授权过期" 
                :value="false"
              />
            </el-select>
          </search-col>

          <search-col>
            <el-select 
              v-model="params.contractSignStatus" 
              placeholder="合同签署" 
              clearable 
              filterable
            >
              <el-option 
                label="已签署" 
                :value="true"
              />
              <el-option 
                label="未签署" 
                :value="false"
              />
            </el-select>
          </search-col>
        </template>
     
        <template #buttons>
          <el-button @click="resetForm">
            <svg-icon icon="tabler:refresh" />重置
          </el-button>
          <el-button type="primary" @click="doSearch">
            <svg-icon icon="lucide:search" />查询
          </el-button>
        </template>
      </search-container>

      <!-- 非搜索区域的额外按钮 -->
      <div class="mb-5">        
        <div class="flex items-center gap-x-2">
          <el-button 
            v-permission="[`crowd:tax:invoice:application`]"
            type="primary" 
            :disabled="selectedIds.size === 0"
            @click="handleApply"
          >
            申请开票
          </el-button>
          <template v-if="isHnOfflineInvoice">
            <el-button 
              v-permission="[`customer:tax:sms:send`]"
              type="warning"
              plain
              class="!ml-0"
              :disabled="selectedIds.size === 0"
              @click="hanldeAllSendCode"
            >
              发送短信
            </el-button>
            <el-button 
              type="warning"
              :disabled="tableData?.length === 0"
              plain
              class="!ml-0"
              @click="exportTable"
            >
              <svg-icon icon="mingcute:file-export-line" />导出
            </el-button>
          </template>
        </div>
        
        <div class="tips">
          <el-button size="small" type="primary" :disabled="ids.length === 0 && total === 0" @click="handleSelectAllBtn">
            {{ selectedIds.size == total && !isZero || selectedIds.size > total && !isZero ? '全不选' : '全选' }}
          </el-button>
          已选择 <span class="text-[var(--color-primary)]">{{ selectedIds.size || 0 }}</span> 条，共 {{ total }} 条
        </div>
      </div>

      <!-- 表格数据 -->
      <el-table ref="tableRef" v-loading="loading" row-key="orderDetailId" class="normal-table" :border="true" :scrollbar-always-on="true" :data="tableData" @select="handleSelect" @select-all="handleSelectAll">
        <el-table-column fixed="left" align="center" type="selection" width="80" />
        <el-table-column align="left" show-overflow-tooltip prop="orderDetailNo" label="子订单编号" :min-width="230" :tooltip-formatter="(scope) => scope?.row?.orderDetailNo">
          <template #default="scope">
            <template v-if="isHnOfflineInvoice">
              <el-tag
                v-if="scope.row.isExceed"
                round
                size="small"
                type="danger"
                class="ml-2"
              >
                超额
              </el-tag>
              <el-tag
                v-if="scope.row.rejected"
                round
                size="small"
                type="warning"
                class="ml-2"
              >
                打回
              </el-tag>
            </template>
            {{ scope.row.orderDetailNo }}
          </template>
        </el-table-column>
        <el-table-column align="left" show-overflow-tooltip prop="companyName" :formatter="(row) => isNull(row.companyName) ? '--' : row.companyName" label="企业名称" :min-width="260" />
        <el-table-column align="left" show-overflow-tooltip prop="personName" :formatter="(row) => isNull(row.personName) ? '--' : row.personName" label="自由职业者" :min-width="180" />
        <el-table-column v-if="!isHnOfflineInvoice" align="center" prop="hnBindStatus" label="海南关系绑定" :min-width="200">
          <template #default="scope">
            <custom-tag :label="scope.row.hnBindStatus === 0 ? '未绑定' : '已绑定'" :type="scope.row.hnBindStatus === 0 ? 'success' : 'danger'" />
          </template>
        </el-table-column>
        <template v-if="isHnOfflineInvoice">
          <el-table-column align="center" prop="hnBindStatus" label="本人授权有效期" :min-width="200">
            <template #default="scope">
              <custom-tag :icon="getAuthTimeIcon(scope.row.hnAuthRemainingDate)" :label="scope.row.hnAuthRemainingDate" :type="getAuthTimeType(scope.row.hnAuthRemainingDate) " />
            </template>
          </el-table-column>
          <el-table-column align="center" prop="hnBindStatus" label="合同签署" :min-width="200" :formatter="(row) => row.contractSignStatus ? '已签署' : '未签署'" />
        </template>
        <el-table-column align="left" show-overflow-tooltip prop="dutyNo" label="任务编号" :min-width="200" />
        <el-table-column align="left" show-overflow-tooltip prop="invoiceSubName" :formatter="(row) => isNull(row.invoiceSubName) ? '--' : row.invoiceSubName" label="订单开票科目" :min-width="200" />
        <el-table-column align="right" show-overflow-tooltip prop="taxAmount" label="含税金额" :min-width="150" />
        <el-table-column align="left" prop="orderStartTime" label="订单创建时间" :min-width="180" />
        <el-table-column align="left" prop="orderEndTime" label="订单完成时间" :min-width="180" />
        
        <el-table-column
          v-if="isHnOfflineInvoice"
          align="center"
          prop="actions"
          label="操作"
          fixed="right"
          :width="100"
        >
          <template #default="scope">
            <el-button link type="primary" @click="hanldeSendCode([scope.row.orderDetailId])">
              发送短信
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 底部分页 -->
      <div class="flex justify-center mt-4">
        <el-pagination
          v-model:current-page="params.page"
          v-model:page-size="params.size"
          :page-sizes="[defaultPageSize, 25, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          background
          @size-change="onSizeChange"
          @current-change="onCurrentPageChange"
        />
      </div>

      <!-- 众包开票申请弹框 -->
      <invoice-application
        :visible="showInvoiceApplication"
        :selected-ids="selectedIds"
        :server-id="serverId"
        :is-hn-offline-invoice="isHnOfflineInvoice"
        @close="showInvoiceApplication = false"
        @confirm="handleConfirm"
      />
    </div>
  </div>
</template>

<script setup lang='ts'>
import InvoiceApplication from '../dialogs/InvoiceApplication.vue';
import { 
  SearchContainer, 
  SearchCol, 
  queryCrowdInvoiceApply,
  queryCrowdInvoiceAndDutyList,
  queryMaintenanceStatus,
  sendSmsCustomerTax,
  queryEnterpriseList,
  exportCrowdInvoiceApply,
} from '@core/main';
import { downloadFile, getAuthTimeIcon, getAuthTimeType, getFomattedTimeRange, isNull } from '@core/tools';
import { SvgIcon, CustomTag, onConfirm } from '@vue3/components';
import { ElMessage, type TableInstance } from 'element-plus';
import { ICrowdInvoiceApplyItem, ICrowdInvoiceApplyParams, ICrowdInvoiceSubjectItem, IEnterpriseItem } from '@core/main/types';
import { computed, nextTick, PropType, reactive, ref, watch } from 'vue';

const props = defineProps({
  source: {
    type: String as PropType<'server' | 'company' | 'sass-crowd' | 'sass-match'>,
    default: 'server',
  },
  serverId: {
    type: Number,
    default: undefined,
  },
  isHnOfflineInvoice: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['confirm']);

const defaultPageSize = 12;

const params = reactive<ICrowdInvoiceApplyParams>({
  page: 1,
  size: defaultPageSize,
});
const tableData = ref<ICrowdInvoiceApplyItem[]>([]);
const companyList = ref<IEnterpriseItem[]>([]);
const loading = ref(false);
const showInvoiceApplication = ref(false);
const total = ref(0);
const tableRef = ref<TableInstance>();
const selectedIds = ref(new Set([]));
const dateRange = ref<string[]>([]);
const dateConfirmRange = ref<string[]>([]);
const invoiceSubjectList = ref<ICrowdInvoiceSubjectItem[]>([]);
const ids = ref<number[]>([]);

const disabledDate = (current: Date) => current.getTime() > Date.now();

const defaultTimeFormat = computed(() => getFomattedTimeRange());
const isZero = computed(() => selectedIds.value.size === 0 && total.value === 0);

async function exportTable() {
  try {
    const { buffer, fileName } = await exportCrowdInvoiceApply(getQueryParams());

    downloadFile(buffer, fileName, null);
    ElMessage.success('导出成功');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function hanldeSendCode(ids: number[]) {
  onConfirm({
    message: ids?.length === 1 ? `确认向“${tableData.value?.find((v) => v.orderDetailId === ids[0])?.personName}”发送短信吗?` : `您已勾选了 ${ids?.length} 名自由职业者，确认批量发送短信吗?`,
    onOk: async () => {
      try {
        await sendSmsCustomerTax(ids);

        ElMessage.success('发送成功');
      } catch (error) {
        if ((error as Error).message) ElMessage.error((error as Error).message);
      }
    },
  });
}

function hanldeAllSendCode() {
  hanldeSendCode(Array.from(selectedIds.value));
}

async function getMaintenanceStatus() {
  try {
    await queryMaintenanceStatus();
    return true;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function handleApply() {
  if (!await getMaintenanceStatus()) return;

  showInvoiceApplication.value = true;
}

function handleConfirm() {
  selectedIds.value = new Set([]);
  restoreSelection();
  
  emit('confirm');
  fetchData();
}

function resetForm() {
  dateRange.value = [];
  dateConfirmRange.value = [];
  selectedIds.value = new Set([]);
  restoreSelection();

  for (const key in params) {
    if (!['page', 'size', 'serverId'].includes(key)) {
      params[key] = undefined;
    }
  }
}

function getQueryParams() {
  return {
    ...params,
    serverId: props.serverId,
    orderStartTimeBegin: dateRange.value ? dateRange.value[0] : undefined,
    orderStartTimeEnd: dateRange.value ? dateRange.value[1] : undefined,
    orderEndTimeBegin: dateConfirmRange.value ? dateConfirmRange.value[0] : undefined,
    orderEndTimeEnd: dateConfirmRange.value ? dateConfirmRange.value[1] : undefined,
  };
}

async function fetchData() {
  loading.value = true;

  try {
    const { data, total: all, ids: idsData } = await queryCrowdInvoiceApply(getQueryParams());

    tableData.value = data;
    total.value = all;
    ids.value = idsData;

    // 恢复当前页选中状态
    nextTick(() => restoreSelection());
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

const handleSelect = (selection, row) => {
  if (selection.includes(row)) {
    selectedIds.value.add(row.orderDetailId);
  } else {
    selectedIds.value.delete(row.orderDetailId);
  }
};

const handleSelectAll = (selection) => {
  const currentPageIds = tableData.value.map((item) => item.orderDetailId);
  
  if (selection.length === 0) {
    currentPageIds.forEach((id) => {
      selectedIds.value.delete(id);
    });
  } else {
    currentPageIds.forEach((id) => {
      selectedIds.value.add(id);
    });
  }
};

const handleSelectAllBtn = () => {
  if (selectedIds.value.size >= total.value) {
    selectedIds.value = new Set([]);
  } else {
    selectedIds.value = new Set(ids.value);
  }
  restoreSelection();
};

const restoreSelection = () => {
  if (!tableRef.value) return;
  
  tableData.value.forEach((row: ICrowdInvoiceApplyItem) => {
    const isSelected = selectedIds.value.has(row.orderDetailId);

    tableRef.value.toggleRowSelection(row, isSelected);
  });
};

function doSearch() {
  params.page = 1;
  fetchData();

  selectedIds.value = new Set([]);
}

function onEnter(e) {
  if (e.keyCode === 13) doSearch();
}

async function fetchList() {
  try {
    const { invoiceSubjects } = await queryCrowdInvoiceAndDutyList(props.serverId);

    invoiceSubjectList.value = invoiceSubjects || [];
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function getCompanyList() {
  try {
    const { data } = await queryEnterpriseList({
      containDraft: 0,
      enable: 1,
      businessId: 3,
    });

    companyList.value = data;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

watch(
  () => props.serverId, 
  (val) => {
    if (val) {
      selectedIds.value = new Set([]);
      restoreSelection();

      fetchList();
      getCompanyList();
      fetchData();
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
:deep(.el-button--warning.is-plain.is-disabled) {
  color: var(--color-primary-disabled);
  background-color: #fff;
  border-color: var(--color-primary-disabled);
  &:hover {
    color: var(--color-primary-disabled);
    background-color: #fff;
    border-color: var(--color-primary-disabled);
  }
}

.tips {
  @apply text-[12px] rounded-[2px] text-[var(--color-text-secondary)] px-[12px] py-[6px] flex items-center gap-x-2 mt-4 bg-[var(--el-color-primary-light-9)] border-[var(--el-color-primary-light-8)] hover:border-[var(--el-color-primary-light-10)];
}
</style>